/**
 * @file Login.js
 * @description Componente de inicio de sesión del sistema de gestión de inventario vehicular
 * @module components/auth/Login
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Car, Lock, Mail, User, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import * as authService from '../../services/authService';

/**
 * Componente Login - Maneja la autenticación de usuarios
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Function} [props.onLoginSuccess] - Callback ejecutado después de login exitoso
 * @returns {JSX.Element} Formulario de inicio de sesión
 */
const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  // Estado del formulario
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isVendedorError, setIsVendedorError] = useState(false);
  /**
   * Efecto para redirigir usuarios autenticados
   * Si el usuario ya está autenticado, redirige al dashboard o página previa
   */
  useEffect(() => {
    if (isAuthenticated && !onLoginSuccess) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location, onLoginSuccess]);

  /**
   * Maneja cambios en los inputs del formulario
   * Elimina espacios en blanco automáticamente para prevenir errores
   * 
   * @param {Event} e - Evento del input
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = value.replace(/\s/g, '');

    setFormData(prev => ({
      ...prev,
      [name]: cleanValue
    }));

    // Limpiar error al editar
    if (error) {
      setError('');
      setIsVendedorError(false);
    }

  };

  /**
   * Maneja el envío del formulario de login
   * Valida credenciales y gestiona la respuesta del servidor
   * 
   * @param {Event} e - Evento del formulario
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setError('Por favor ingresa usuario y contraseña');
      return;
    }

    setIsLoading(true);
    setError('');
    setIsVendedorError(false);

    try {
      const result = await authService.login(formData.username, formData.password);

      if (result.success) {
        if (onLoginSuccess) {
          onLoginSuccess(result.user);
        } else {
          login();
          setTimeout(() => {
            // CAMBIO: tipo_usuario === 'vendedor' en lugar de rol === 'ventas'
            const isVendedor = result.user?.tipo_usuario === 'vendedor';
            const defaultPath = isVendedor ? '/sales-dashboard' : '/dashboard';
            const from = location.state?.from?.pathname || defaultPath;
            navigate(from, { replace: true });
          }, 0);
        }
      } else {
        setError(result.message || 'Credenciales incorrectas. Verifica tu usuario y contraseña.');
      }
    } catch (err) {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Validación del formulario
  const isFormValid = formData.username.length > 0 && formData.password.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo y título */}
        <div className="text-center mb-8">
          <img
            src="/logos/CDA2.png"
            alt="Grupo Continental"
            className="h-40 w-auto object-contain mx-auto mb-2"
            style={{ mixBlendMode: 'multiply' }}
          />
          <p className="text-gray-500 text-sm">
            Inventario / Ubicación
          </p>
        </div>

        {/* Formulario de login */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Iniciar Sesión
            </h2>
            <p className="text-gray-600 text-sm">
              Ingresa tus credenciales para acceder al sistema
            </p>
          </div>

          {/* Mensaje de error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-800 font-medium">
                  Error al iniciar sesión
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {error}
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo Usuario */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Usuario
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {formData.username.includes('@') ? (
                    <Mail className="h-5 w-5 text-gray-400" />
                  ) : (
                    <User className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-gray-900 placeholder-gray-500 ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Ingresa tu usuario..."
                  autoComplete="username"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Campo Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-gray-900 placeholder-gray-500 ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Ingresa tu contraseña..."
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Botón de submit */}
            <button
              type="submit"
              disabled={!isFormValid || isLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${isFormValid && !isLoading
                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Ingresando...
                </div>
              ) : (
                'Ingresar'
              )}
            </button>
          </form>

          {/* Información adicional */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Sistema de gestión de inventario vehicular
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;